<script setup>
import CaretDownIcon from '@iconify-vue/ph/caret-down';
import CaretUpDownIcon from '@iconify-vue/ph/caret-up-down';
import CodeIcon from '@iconify-vue/ph/code';
import ImageIcon from '@iconify-vue/ph/image';
import LinkIcon from '@iconify-vue/ph/link';
import ListBulletsIcon from '@iconify-vue/ph/list-bullets';
import ListNumbersIcon from '@iconify-vue/ph/list-numbers';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import TextAlignCenterIcon from '@iconify-vue/ph/text-align-center';
import TextAlignJustifyIcon from '@iconify-vue/ph/text-align-justify';
import TextAlignLeftIcon from '@iconify-vue/ph/text-align-left';
import TextAlignRightIcon from '@iconify-vue/ph/text-align-right';
import TextBIcon from '@iconify-vue/ph/text-b';
import TextItalicIcon from '@iconify-vue/ph/text-italic';
import TextStrikethroughIcon from '@iconify-vue/ph/text-strikethrough';
import TextUnderlineIcon from '@iconify-vue/ph/text-underline';
import ManageMediasAction from '@js/components/actions/ManageMediasAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DialogModal from '@js/components/dialogs/DialogModal.vue';
import InputField from '@js/components/fields/InputField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import ColorPicker from '@js/components/pickers/ColorPicker.vue';
import Bold from '@tiptap/extension-bold';
import Document from '@tiptap/extension-document';
import FileHandler from '@tiptap/extension-file-handler';
import HardBreak from '@tiptap/extension-hard-break';
import Heading from '@tiptap/extension-heading';
import Highlight from '@tiptap/extension-highlight';
import History from '@tiptap/extension-history';
import Image from '@tiptap/extension-image';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import { BulletList, ListItem, OrderedList } from '@tiptap/extension-list';
import Paragraph from '@tiptap/extension-paragraph';
import Strike from '@tiptap/extension-strike';
import {
    Table,
    TableCell,
    TableHeader,
    TableRow,
} from '@tiptap/extension-table';
import Text from '@tiptap/extension-text';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyleKit } from '@tiptap/extension-text-style';
import Typography from '@tiptap/extension-typography';
import Underline from '@tiptap/extension-underline';
import { Dropcursor, Gapcursor, Placeholder } from '@tiptap/extensions';
import { EditorContent, useEditor } from '@tiptap/vue-3';
import { Motion } from 'motion-v';
import {
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuPortal,
    DropdownMenuRoot,
    DropdownMenuTrigger,
} from 'reka-ui';
import { ErrorMessage, Field } from 'vee-validate';
import { computed, ref, watch } from 'vue';

const {
    fieldId,
    fieldName,
    required = false,
    label,
    placeholder = '',
    disabled = false,
    size = 'medium',
    mediaPath = 'media',
    disableMedia = false,
    disableHtmlToggle = false,
} = defineProps({
    fieldId: {
        type: String,
        required: true,
    },
    fieldName: {
        type: String,
        required: true,
    },
    required: {
        type: Boolean,
        required: false,
    },
    label: {
        type: String,
        required: false,
    },
    placeholder: {
        type: String,
        required: false,
    },
    disabled: {
        type: Boolean,
        required: false,
    },
    size: {
        type: String,
        required: false,
    },
    mediaPath: {
        type: String,
        required: false,
    },
    disableMedia: {
        type: Boolean,
        required: false,
    },
    disableHtmlToggle: {
        type: Boolean,
        required: false,
    },
});

const model = defineModel();

const editor = useEditor({
    content: model.value,
    extensions: [
        Document,
        Paragraph,
        Placeholder.configure({
            placeholder,
            emptyNodeClass: 'my-custom-is-empty-class',
        }),
        Typography,
        Text,
        Heading,
        Bold,
        Italic,
        Underline,
        Strike,
        ListItem,
        BulletList,
        OrderedList,
        HardBreak,
        Image.configure({
            allowBase64: true,
        }),
        FileHandler.configure({
            allowedMimeTypes: [
                'image/png',
                'image/jpeg',
                'image/gif',
                'image/webp',
            ],
            onPaste: () => {
                imageModalIsOpen.value = true;
            },
        }),
        TextAlign.configure({
            types: ['heading', 'paragraph'],
        }),
        Link.configure({
            openOnClick: false,
            HTMLAttributes: {
                target: null,
                rel: null,
            },
        }),
        TextStyleKit.configure({
            color: {
                types: ['textStyle'],
            },
        }),
        Highlight.configure({ multicolor: true }),
        History,
        Gapcursor,
        Dropcursor,
        Table.configure({
            resizable: true,
        }),
        TableRow,
        TableHeader,
        TableCell,
    ],
    onUpdate: ({ editor }) => {
        model.value = editor.isEmpty ? '' : editor.getHTML();
    },
    editorProps: {
        transformPastedText(text) {
            text = text.replace(/’/g, "'");
            text = text.replace(/—/g, '-');

            return text;
        },
        transformPastedHTML(html) {
            html = html.replace(/’/g, "'");
            html = html.replace(/—/g, '-');

            html = html.replace(/<a([^>]+)target="[^"]*"([^>]*)>/gi, '<a$1$2>');
            html = html.replace(/<a([^>]+)rel="[^"]*"([^>]*)>/gi, '<a$1$2>');

            return html;
        },
    },
    editable: !disabled,
});

const PRESET_COLORS = [
    '#000000',
    '#cccccc',
    '#ffff00',
    '#00ff00',
    '#00ffff',
    '#ff00ff',
    '#ff9900',
    '#ff0000',
];

const color = ref('#000000');

const linkModalIsOpen = ref(false);

const linkUrl = ref('');

const linkTargetBlank = ref(true);

const imageModalIsOpen = ref(false);

const showHtml = ref(false);

const colorIsDark = computed(() => {
    let c = color.value.substring(1);
    let rgb = parseInt(c, 16);
    let r = (rgb >> 16) & 0xff;
    let g = (rgb >> 8) & 0xff;
    let b = (rgb >> 0) & 0xff;

    let luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;

    return luma < 40;
});

watch(linkModalIsOpen, (value) => {
    if (value && editor.value.isActive('link')) {
        linkUrl.value = editor.value.getAttributes('link').href;
        linkTargetBlank.value =
            editor.value.getAttributes('link').target === '_blank';
    } else {
        linkUrl.value = '';
        linkTargetBlank.value = true;
    }
});

function createLink() {
    editor.value
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({
            href: linkUrl.value,
            target: linkTargetBlank.value ? '_blank' : null,
            rel: null,
        })
        .run();

    linkUrl.value = '';
    linkTargetBlank.value = true;

    linkModalIsOpen.value = false;
}

function deleteLink() {
    editor.value.chain().focus().extendMarkRange('link').unsetLink().run();

    linkModalIsOpen.value = false;
}

function toggleShowHtml() {
    if (showHtml.value) {
        editor.value.commands.setContent(model.value);

        if (editor.value.isEmpty) {
            model.value = '';
        }
    }

    showHtml.value = !showHtml.value;
}

function onImageSelect(file) {
    editor.value.chain().focus().setImage({ src: file.url }).run();
}

const editorContentRef = ref(null);

const textareaRef = ref(null);

const contentHeight = ref(null);

const resizable = computed(() => size !== 'full');

const contentStyle = computed(() =>
    contentHeight.value ? { height: `${contentHeight.value}px` } : null,
);

let resizeStartY = 0;

let resizeStartHeight = 0;

function onResizeStart(event) {
    const element = showHtml.value
        ? textareaRef.value
        : editorContentRef.value?.$el;

    if (!element) {
        return;
    }

    event.preventDefault();

    resizeStartY = event.clientY;
    resizeStartHeight = element.offsetHeight;

    event.currentTarget.setPointerCapture(event.pointerId);
}

function onResizeMove(event) {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
        return;
    }

    contentHeight.value = Math.max(
        120,
        resizeStartHeight + event.clientY - resizeStartY,
    );
}

function onResizeEnd(event) {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
    }
}

watch(
    () => disabled,
    (value) => {
        editor.value?.setEditable(!value);
    },
);
</script>

<template>
    <div
        class="mb-4 flex flex-col"
        :class="{ 'min-h-0 flex-1': size === 'full' }"
    >
        <label v-if="label" :for="fieldName" class="mb-2">
            {{ label }}
            <sup v-if="required" class="text-red-500">*</sup>
        </label>

        <Field v-model="model" :id="fieldId" :name="fieldName">
            <div
                v-if="editor"
                class="w-full"
                :class="{
                    'cursor-not-allowed opacity-40': disabled,
                    'flex min-h-0 flex-1 flex-col': size === 'full',
                }"
            >
                <div
                    class="relative z-10 -mb-px flex w-full flex-wrap gap-4 rounded-t-sm border border-gray-300 bg-white px-5 py-3"
                >
                    <DropdownMenuRoot v-if="!showHtml">
                        <DropdownMenuTrigger
                            type="button"
                            class="group inline-flex size-8 w-44 cursor-pointer items-center justify-between gap-2 rounded-md border border-gray-200 bg-white px-2 py-1 font-bold outline-0 duration-150 enabled:hover:bg-gray-200"
                            :disabled="!editor.isEditable"
                        >
                            <template
                                v-if="editor.isActive('heading', { level: 1 })"
                            >
                                Titre 1
                            </template>
                            <template
                                v-else-if="
                                    editor.isActive('heading', { level: 2 })
                                "
                            >
                                Titre 2
                            </template>
                            <template
                                v-else-if="
                                    editor.isActive('heading', { level: 3 })
                                "
                            >
                                Titre 3
                            </template>
                            <template
                                v-else-if="
                                    editor.isActive('heading', { level: 4 })
                                "
                            >
                                Titre 4
                            </template>
                            <template
                                v-else-if="
                                    editor.isActive('heading', { level: 5 })
                                "
                            >
                                Titre 5
                            </template>
                            <template
                                v-else-if="
                                    editor.isActive('heading', { level: 6 })
                                "
                            >
                                Titre 6
                            </template>
                            <template v-else-if="editor.isActive('paragraph')">
                                Paragraphe
                            </template>
                            <template v-else> Paragraphe</template>

                            <CaretDownIcon
                                class="size-4 shrink-0 duration-150 group-data-[state=open]:rotate-180"
                            />
                        </DropdownMenuTrigger>

                        <DropdownMenuPortal>
                            <DropdownMenuContent
                                align="start"
                                :side-offset="6"
                                class="z-50"
                                @close-auto-focus.prevent
                            >
                                <Motion
                                    :initial="{ opacity: 0, y: -4 }"
                                    :animate="{ opacity: 1, y: 0 }"
                                    :transition="{ duration: 0.15 }"
                                    class="flex w-(--reka-dropdown-menu-trigger-width) flex-col overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm"
                                >
                                    <DropdownMenuItem
                                        class="w-full cursor-pointer px-3 py-1 text-left text-sm font-normal whitespace-nowrap outline-0 duration-150 hover:bg-gray-200 data-highlighted:bg-gray-200"
                                        :class="{
                                            'bg-gray-200': editor.isActive(
                                                'heading',
                                                {
                                                    level: 1,
                                                },
                                            ),
                                        }"
                                        @click="
                                            editor
                                                .chain()
                                                .focus()
                                                .toggleHeading({ level: 1 })
                                                .run()
                                        "
                                    >
                                        Titre 1
                                    </DropdownMenuItem>

                                    <DropdownMenuItem
                                        class="w-full cursor-pointer px-3 py-1 text-left text-sm font-normal whitespace-nowrap outline-0 duration-150 hover:bg-gray-200 data-highlighted:bg-gray-200"
                                        :class="{
                                            'bg-gray-200': editor.isActive(
                                                'heading',
                                                {
                                                    level: 2,
                                                },
                                            ),
                                        }"
                                        @click="
                                            editor
                                                .chain()
                                                .focus()
                                                .toggleHeading({ level: 2 })
                                                .run()
                                        "
                                    >
                                        Titre 2
                                    </DropdownMenuItem>

                                    <DropdownMenuItem
                                        class="w-full cursor-pointer px-3 py-1 text-left text-sm font-normal whitespace-nowrap outline-0 duration-150 hover:bg-gray-200 data-highlighted:bg-gray-200"
                                        :class="{
                                            'bg-gray-200': editor.isActive(
                                                'heading',
                                                {
                                                    level: 3,
                                                },
                                            ),
                                        }"
                                        @click="
                                            editor
                                                .chain()
                                                .focus()
                                                .toggleHeading({ level: 3 })
                                                .run()
                                        "
                                    >
                                        Titre 3
                                    </DropdownMenuItem>

                                    <DropdownMenuItem
                                        class="w-full cursor-pointer px-3 py-1 text-left text-sm font-normal whitespace-nowrap outline-0 duration-150 hover:bg-gray-200 data-highlighted:bg-gray-200"
                                        :class="{
                                            'bg-gray-200': editor.isActive(
                                                'heading',
                                                {
                                                    level: 4,
                                                },
                                            ),
                                        }"
                                        @click="
                                            editor
                                                .chain()
                                                .focus()
                                                .toggleHeading({ level: 4 })
                                                .run()
                                        "
                                    >
                                        Titre 4
                                    </DropdownMenuItem>

                                    <DropdownMenuItem
                                        class="w-full cursor-pointer px-3 py-1 text-left text-sm font-normal whitespace-nowrap outline-0 duration-150 hover:bg-gray-200 data-highlighted:bg-gray-200"
                                        :class="{
                                            'bg-gray-200': editor.isActive(
                                                'heading',
                                                {
                                                    level: 5,
                                                },
                                            ),
                                        }"
                                        @click="
                                            editor
                                                .chain()
                                                .focus()
                                                .toggleHeading({ level: 5 })
                                                .run()
                                        "
                                    >
                                        Titre 5
                                    </DropdownMenuItem>

                                    <DropdownMenuItem
                                        class="w-full cursor-pointer px-3 py-1 text-left text-sm font-normal whitespace-nowrap outline-0 duration-150 hover:bg-gray-200 data-highlighted:bg-gray-200"
                                        :class="{
                                            'bg-gray-200': editor.isActive(
                                                'heading',
                                                {
                                                    level: 6,
                                                },
                                            ),
                                        }"
                                        @click="
                                            editor
                                                .chain()
                                                .focus()
                                                .toggleHeading({ level: 6 })
                                                .run()
                                        "
                                    >
                                        Titre 6
                                    </DropdownMenuItem>

                                    <DropdownMenuItem
                                        class="w-full cursor-pointer px-3 py-1 text-left text-sm font-normal whitespace-nowrap outline-0 duration-150 hover:bg-gray-200 data-highlighted:bg-gray-200"
                                        :class="{
                                            'bg-gray-200':
                                                editor.isActive('paragraph'),
                                        }"
                                        @click="editor.commands.setParagraph()"
                                    >
                                        Paragraphe
                                    </DropdownMenuItem>
                                </Motion>
                            </DropdownMenuContent>
                        </DropdownMenuPortal>
                    </DropdownMenuRoot>

                    <DropdownMenuRoot v-if="!showHtml">
                        <DropdownMenuTrigger
                            type="button"
                            class="group inline-flex size-8 w-16 cursor-pointer items-center justify-between gap-2 rounded-md border border-gray-200 bg-white px-2 py-1 font-bold outline-0 duration-150 enabled:hover:bg-gray-200"
                            :disabled="!editor.isEditable"
                        >
                            <template
                                v-if="editor.isActive({ textAlign: 'left' })"
                            >
                                <TextAlignLeftIcon class="size-4" />
                            </template>
                            <template
                                v-else-if="
                                    editor.isActive({ textAlign: 'center' })
                                "
                            >
                                <TextAlignCenterIcon class="size-4" />
                            </template>
                            <template
                                v-else-if="
                                    editor.isActive({ textAlign: 'right' })
                                "
                            >
                                <TextAlignRightIcon class="size-4" />
                            </template>
                            <template
                                v-else-if="
                                    editor.isActive({ textAlign: 'justify' })
                                "
                            >
                                <TextAlignJustifyIcon class="size-4" />
                            </template>
                            <template v-else>
                                <TextAlignLeftIcon class="size-4" />
                            </template>

                            <CaretDownIcon
                                class="size-4 shrink-0 duration-150 group-data-[state=open]:rotate-180"
                            />
                        </DropdownMenuTrigger>

                        <DropdownMenuPortal>
                            <DropdownMenuContent
                                align="start"
                                :side-offset="6"
                                class="z-50"
                                @close-auto-focus.prevent
                            >
                                <Motion
                                    :initial="{ opacity: 0, y: -4 }"
                                    :animate="{ opacity: 1, y: 0 }"
                                    :transition="{ duration: 0.15 }"
                                    class="flex w-(--reka-dropdown-menu-trigger-width) flex-col overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm"
                                >
                                    <DropdownMenuItem
                                        class="w-full cursor-pointer px-3 py-1 text-left text-sm font-normal whitespace-nowrap outline-0 duration-150 hover:bg-gray-200 data-highlighted:bg-gray-200"
                                        :class="{
                                            'bg-gray-200': editor.isActive({
                                                textAlign: 'left',
                                            }),
                                        }"
                                        @click="
                                            editor
                                                .chain()
                                                .focus()
                                                .setTextAlign('left')
                                                .run()
                                        "
                                    >
                                        <TextAlignLeftIcon class="size-4" />
                                    </DropdownMenuItem>

                                    <DropdownMenuItem
                                        class="w-full cursor-pointer px-3 py-1 text-left text-sm font-normal whitespace-nowrap outline-0 duration-150 hover:bg-gray-200 data-highlighted:bg-gray-200"
                                        :class="{
                                            'bg-gray-200': editor.isActive({
                                                textAlign: 'center',
                                            }),
                                        }"
                                        @click="
                                            editor
                                                .chain()
                                                .focus()
                                                .setTextAlign('center')
                                                .run()
                                        "
                                    >
                                        <TextAlignCenterIcon class="size-4" />
                                    </DropdownMenuItem>

                                    <DropdownMenuItem
                                        class="w-full cursor-pointer px-3 py-1 text-left text-sm font-normal whitespace-nowrap outline-0 duration-150 hover:bg-gray-200 data-highlighted:bg-gray-200"
                                        :class="{
                                            'bg-gray-200': editor.isActive({
                                                textAlign: 'right',
                                            }),
                                        }"
                                        @click="
                                            editor
                                                .chain()
                                                .focus()
                                                .setTextAlign('right')
                                                .run()
                                        "
                                    >
                                        <TextAlignRightIcon class="size-4" />
                                    </DropdownMenuItem>

                                    <DropdownMenuItem
                                        class="w-full cursor-pointer px-3 py-1 text-left text-sm font-normal whitespace-nowrap outline-0 duration-150 hover:bg-gray-200 data-highlighted:bg-gray-200"
                                        :class="{
                                            'bg-gray-200': editor.isActive({
                                                textAlign: 'justify',
                                            }),
                                        }"
                                        @click="
                                            editor
                                                .chain()
                                                .focus()
                                                .setTextAlign('justify')
                                                .run()
                                        "
                                    >
                                        <TextAlignJustifyIcon class="size-4" />
                                    </DropdownMenuItem>
                                </Motion>
                            </DropdownMenuContent>
                        </DropdownMenuPortal>
                    </DropdownMenuRoot>

                    <div v-if="!showHtml" class="flex gap-2">
                        <button
                            type="button"
                            class="size-8 cursor-pointer rounded-md border border-gray-200 bg-white px-2 py-1 duration-150 focus:outline-0 enabled:hover:bg-gray-200"
                            :class="{
                                'bg-gray-200!': editor.isActive('bold'),
                            }"
                            :disabled="!editor.isEditable"
                            @click="editor.chain().focus().toggleBold().run()"
                        >
                            <TextBIcon class="size-4" />
                        </button>

                        <button
                            type="button"
                            class="size-8 cursor-pointer rounded-md border border-gray-200 px-2 py-1 duration-150 focus:bg-gray-100 focus:outline-0 enabled:hover:bg-gray-200"
                            :class="{
                                'bg-gray-200!': editor.isActive('italic'),
                            }"
                            :disabled="!editor.isEditable"
                            @click="editor.chain().focus().toggleItalic().run()"
                        >
                            <TextItalicIcon class="size-4" />
                        </button>

                        <button
                            type="button"
                            class="size-8 cursor-pointer rounded-md border border-gray-200 bg-white px-2 py-1 duration-150 focus:outline-0 enabled:hover:bg-gray-200"
                            :class="{
                                'bg-gray-200!': editor.isActive('underline'),
                            }"
                            :disabled="!editor.isEditable"
                            @click="
                                editor.chain().focus().toggleUnderline().run()
                            "
                        >
                            <TextUnderlineIcon class="size-4" />
                        </button>

                        <button
                            type="button"
                            class="size-8 cursor-pointer rounded-md border border-gray-200 bg-white px-2 py-1 duration-150 focus:outline-0 enabled:hover:bg-gray-200"
                            :class="{
                                'bg-gray-200!': editor.isActive('strike'),
                            }"
                            :disabled="!editor.isEditable"
                            @click="editor.chain().focus().toggleStrike().run()"
                        >
                            <TextStrikethroughIcon class="size-4" />
                        </button>
                    </div>

                    <div v-if="!showHtml" class="flex gap-2">
                        <button
                            type="button"
                            class="size-8 cursor-pointer rounded-md border border-gray-200 bg-white px-2 py-1 duration-150 focus:outline-0 enabled:hover:bg-gray-200"
                            :class="{
                                'bg-gray-200!': editor.isActive('bulletList'),
                            }"
                            :disabled="!editor.isEditable"
                            @click="
                                editor.chain().focus().toggleBulletList().run()
                            "
                        >
                            <ListBulletsIcon class="size-4" />
                        </button>

                        <button
                            type="button"
                            class="size-8 cursor-pointer rounded-md border border-gray-200 bg-white px-2 py-1 duration-150 focus:outline-0 enabled:hover:bg-gray-200"
                            :class="{
                                'bg-gray-200!': editor.isActive('orderedList'),
                            }"
                            :disabled="!editor.isEditable"
                            @click="
                                editor.chain().focus().toggleOrderedList().run()
                            "
                        >
                            <ListNumbersIcon class="size-4" />
                        </button>
                    </div>

                    <div v-if="!showHtml" class="flex gap-2">
                        <ColorPicker
                            v-model="color"
                            :disabled="!editor.isEditable"
                            :preset-colors="PRESET_COLORS"
                        >
                            <template #trigger>
                                <button
                                    type="button"
                                    class="flex h-8 w-16 cursor-pointer items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 duration-150 focus:outline-0 enabled:hover:bg-gray-200 disabled:cursor-not-allowed"
                                    :disabled="!editor.isEditable"
                                >
                                    <span
                                        class="size-4 shrink-0 rounded-sm border border-black/10"
                                        :style="{ backgroundColor: color }"
                                    ></span>

                                    <CaretUpDownIcon class="ml-auto size-4" />
                                </button>
                            </template>
                        </ColorPicker>

                        <button
                            type="button"
                            class="size-8 cursor-pointer rounded-md border border-gray-200 bg-white px-2 py-1 duration-150 focus:outline-0 enabled:hover:bg-gray-200"
                            :class="{
                                'bg-gray-200!': editor.isActive('textStyle', {
                                    color,
                                }),
                            }"
                            :disabled="!editor.isEditable"
                            @click="
                                editor.chain().focus().setColor(color).run()
                            "
                        >
                            <span
                                class="inline-block size-4 leading-4 underline decoration-2"
                                :style="'text-decoration-color: ' + color"
                            >
                                A
                            </span>
                        </button>

                        <button
                            type="button"
                            class="size-8 cursor-pointer rounded-md border border-gray-200 bg-white px-2 py-1 duration-150 focus:outline-0 enabled:hover:bg-gray-200"
                            :class="{
                                'bg-gray-200!': editor.isActive('highlight', {
                                    color,
                                }),
                            }"
                            :disabled="!editor.isEditable"
                            @click="
                                editor
                                    .chain()
                                    .focus()
                                    .toggleHighlight({ color })
                                    .run()
                            "
                        >
                            <span
                                class="inline-block size-4 leading-4"
                                :class="{
                                    'text-white': colorIsDark,
                                    'text-black': !colorIsDark,
                                }"
                                :style="'background-color: ' + color"
                            >
                                A
                            </span>
                        </button>
                    </div>

                    <ManageMediasAction
                        v-if="!showHtml && !disableMedia"
                        v-model="imageModalIsOpen"
                        selectable
                        :path="mediaPath"
                        @select="onImageSelect"
                    >
                        <template #trigger>
                            <button
                                type="button"
                                class="size-8 cursor-pointer rounded-md border border-gray-200 bg-white px-2 py-1 duration-150 focus:outline-0 enabled:hover:bg-gray-200"
                                :disabled="!editor.isEditable"
                            >
                                <ImageIcon class="size-4" />
                            </button>
                        </template>
                    </ManageMediasAction>

                    <button
                        v-if="!showHtml"
                        type="button"
                        class="size-8 cursor-pointer rounded-md border border-gray-200 bg-white px-2 py-1 duration-150 focus:outline-0 enabled:hover:bg-gray-200"
                        :disabled="!editor.isEditable"
                        :class="{
                            'bg-gray-200!': editor.isActive('link'),
                        }"
                        @click="linkModalIsOpen = true"
                    >
                        <LinkIcon class="size-4" />
                    </button>

                    <button
                        v-if="!disableHtmlToggle"
                        type="button"
                        class="size-8 cursor-pointer rounded-md border border-gray-200 bg-white px-2 py-1 duration-150 focus:outline-0 enabled:hover:bg-gray-200"
                        :disabled="!editor.isEditable"
                        @click="toggleShowHtml"
                    >
                        <CodeIcon v-if="!showHtml" class="size-4" />
                        <PencilSimpleIcon v-else class="size-4" />
                    </button>

                    <div
                        v-if="$slots['top-right-actions']"
                        class="ml-auto flex items-center gap-2"
                    >
                        <slot name="top-right-actions" />
                    </div>
                </div>

                <EditorContent
                    ref="editorContentRef"
                    :editor="editor"
                    class="wysiwyg w-full overflow-auto border border-gray-300 bg-white px-5 py-3 ring-1 ring-transparent duration-150 focus-within:ring-gray-300 focus-within:outline-hidden"
                    :style="contentStyle"
                    :class="{
                        hidden: showHtml,
                        'rounded-b': !resizable,
                        'min-h-0 flex-1': size === 'full',
                        'h-60': size === 'very-small',
                        'h-80': size === 'small',
                        'h-160': size === 'medium',
                        'h-240': size === 'large',
                    }"
                />

                <textarea
                    ref="textareaRef"
                    v-model="model"
                    :disabled="disabled"
                    class="block w-full resize-none overflow-auto border border-gray-300 px-5 py-3 ring-1 ring-transparent duration-150 focus:ring-gray-300 focus:outline-hidden disabled:cursor-not-allowed"
                    :style="contentStyle"
                    :class="{
                        hidden: !showHtml,
                        'rounded-b': !resizable,
                        'min-h-0 flex-1': size === 'full',
                        'h-80': size === 'small',
                        'h-160': size === 'medium',
                        'h-240': size === 'large',
                    }"
                ></textarea>

                <div
                    v-if="resizable"
                    role="separator"
                    aria-orientation="horizontal"
                    title="Redimensionner l'éditeur"
                    class="-mt-px flex h-3 w-full shrink-0 cursor-ns-resize touch-none items-center justify-center rounded-b border border-gray-300 bg-gray-50 duration-150 hover:bg-gray-100"
                    @pointerdown="onResizeStart"
                    @pointermove="onResizeMove"
                    @pointerup="onResizeEnd"
                    @pointercancel="onResizeEnd"
                >
                    <span class="h-0.5 w-8 rounded-full bg-gray-300"></span>
                </div>
            </div>
        </Field>

        <ErrorMessage :name="fieldName" class="mt-1 text-sm text-red-500" />
    </div>

    <DialogModal v-model="linkModalIsOpen" size="small">
        <template #title> Modifier un lien </template>

        <template #content>
            <FormContent>
                <InputField
                    v-model="linkUrl"
                    label="Lien URL"
                    field-name="linkUrl"
                    field-id="linkUrl"
                    required
                />

                <SwitchField
                    v-model="linkTargetBlank"
                    label="Ouvrir le lien dans un nouvel onglet"
                    field-name="linkTargetBlank"
                    field-id="linkTargetBlank"
                />
            </FormContent>
        </template>

        <template #footer>
            <div class="flex gap-2">
                <ActionButton
                    v-if="editor.getAttributes('link').href"
                    :callback="deleteLink"
                >
                    Supprimer le lien
                </ActionButton>

                <ActionButton :callback="createLink">
                    Créer un lien
                </ActionButton>
            </div>
        </template>
    </DialogModal>
</template>

<style scoped>
:deep(.tiptap) {
    height: 100%;
    outline: 0;
}

:deep(.tiptap) p.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    float: left;
    color: #b0b0b0;
    pointer-events: none;
    height: 0;
}

:deep(.tiptap) {
    img.ProseMirror-selectednode {
        outline: 2px solid #2563eb;
    }
}
</style>
